import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import ParentLayout from '../components/ParentLayout';
import { 
  Users, Phone, Mail, Search, Filter, ChevronDown,
  User, Shield, Award, Heart, Star, Briefcase, MessageSquare
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Department display config
const DEPARTMENT_CONFIG = {
  board: { label: 'Board of Directors', icon: Shield, color: 'bg-navy', showEmail: true },
  coaching: { label: 'Coaching Staff', icon: Award, color: 'bg-red', showEmail: false },
  operations: { label: 'Operations', icon: Briefcase, color: 'bg-light-blue', showEmail: false },
  volunteer: { label: 'Volunteer Leadership', icon: Heart, color: 'bg-gold', showEmail: false }
};

// Department contacts to display (only these will show)
const VISIBLE_DEPT_CONTACTS = ['Registration', 'Sponsorships', 'General Inquiries'];

// Role display labels
const ROLE_LABELS = {
  president: 'President',
  vice_president: 'Vice President',
  secretary: 'Secretary',
  treasurer: 'Treasurer',
  board_member: 'Board Member',
  head_coach: 'Head Coach',
  assistant_coach: 'Assistant Coach',
  flag_coach: 'Flag Football Coach',
  cheer_coach: 'Cheer Coach',
  coordinator: 'Coordinator',
  equipment_manager: 'Equipment Manager',
  registrar: 'Registrar',
  team_mom: 'Team Mom',
  volunteer_coordinator: 'Volunteer Coordinator',
  other: 'Staff'
};

const StaffDirectoryPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [staff, setStaff] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterDept, setFilterDept] = useState('all');
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [staffRes, deptRes] = await Promise.all([
        fetch(`${API_URL}/api/staff`, { credentials: 'include' }),
        fetch(`${API_URL}/api/departments`, { credentials: 'include' })
      ]);

      if (staffRes.ok) {
        const staffData = await staffRes.json();
        setStaff(staffData.staff || []);
      }
      if (deptRes.ok) {
        const deptData = await deptRes.json();
        setDepartments(deptData.departments || []);
      }
    } catch (error) {
      console.error('Error fetching directory:', error);
    } finally {
      setLoading(false);
    }
  };

  // Group staff by department
  const groupedStaff = staff.reduce((acc, member) => {
    const dept = member.department || 'other';
    if (!acc[dept]) acc[dept] = [];
    acc[dept].push(member);
    return acc;
  }, {});

  // Filter staff
  const filteredStaff = staff.filter(member => {
    const matchesSearch = member.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      member.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      member.email?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesDept = filterDept === 'all' || member.department === filterDept;
    return matchesSearch && matchesDept;
  });

  // Group filtered staff
  const filteredGrouped = filteredStaff.reduce((acc, member) => {
    const dept = member.department || 'other';
    if (!acc[dept]) acc[dept] = [];
    acc[dept].push(member);
    return acc;
  }, {});

  const StaffCard = ({ member }) => {
    const config = DEPARTMENT_CONFIG[member.department] || { color: 'bg-gray-500', showEmail: false };
    const showEmail = config.showEmail === true;
    
    const handleDirectMessage = async () => {
      // Try to find the user by email and navigate to Communications DM
      if (member.email) {
        try {
          const res = await axios.get(`${API_URL}/api/dm/users?search=${encodeURIComponent(member.email)}`, { withCredentials: true });
          const matchingUser = res.data.find(u => u.email === member.email);
          if (matchingUser) {
            navigate(`/communications?tab=messages&dm_user=${matchingUser.user_id}`);
            return;
          }
        } catch (error) {
          console.error('Failed to find user:', error);
        }
      }
      // Fallback: just navigate to Communications messages tab
      navigate('/communications?tab=messages');
    };
    
    return (
      <div 
        className="bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-lg hover:-translate-y-1 transition-all duration-200"
        data-testid={`staff-card-${member.staff_id}`}
      >
        <div className={`h-1.5 ${config.color}`} />
        <div className="p-4">
          <div className="flex items-start space-x-4">
            {/* Photo */}
            <div className="flex-shrink-0">
              {member.photo_url ? (
                <img 
                  src={member.photo_url} 
                  alt={member.name}
                  className="w-16 h-16 rounded-xl object-cover border-2 border-gray-100 shadow-sm"
                />
              ) : (
                <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-navy to-light-blue flex items-center justify-center shadow-sm">
                  <span className="text-white font-display text-lg font-black">
                    {member.name?.split(' ').map(n => n[0]).join('').slice(0, 2)}
                  </span>
                </div>
              )}
            </div>
            
            {/* Info */}
            <div className="flex-1 min-w-0">
              <h3 className="font-bold text-navy truncate">{member.name}</h3>
              <p className="text-sm text-gray-600 font-medium">{member.title}</p>
              {member.age_group && (
                <span className="inline-block mt-1 px-2 py-0.5 bg-gold/20 text-navy text-xs rounded-full font-bold uppercase tracking-wider">
                  {member.age_group}
                </span>
              )}
            </div>
          </div>
          
          {/* Contact Info - Conditional based on department */}
          <div className="mt-4 space-y-2">
            {/* Show email only for Board of Directors */}
            {showEmail && member.email && (
              <a 
                href={`mailto:${member.email}`}
                className="flex items-center text-sm text-gray-600 hover:text-navy transition-colors"
              >
                <Mail className="w-4 h-4 mr-2 text-gray-400" />
                <span className="truncate">{member.email}</span>
              </a>
            )}
            
            {/* Show Direct Message button for non-board members */}
            {!showEmail && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleDirectMessage}
                className="w-full flex items-center justify-center border-2 border-navy/20 text-navy hover:bg-navy hover:text-white hover:border-navy font-bold uppercase tracking-wider text-xs transition-all duration-200"
                data-testid={`dm-btn-${member.staff_id}`}
              >
                <MessageSquare className="w-4 h-4 mr-2" />
                Direct Message
              </Button>
            )}
            
            {member.phone && (
              <a 
                href={`tel:${member.phone}`}
                className="flex items-center text-sm text-gray-600 hover:text-navy transition-colors"
              >
                <Phone className="w-4 h-4 mr-2 text-gray-400" />
                <span>{member.phone}</span>
              </a>
            )}
          </div>

          {/* Bio */}
          {member.bio && (
            <p className="mt-3 text-sm text-gray-500 line-clamp-2 font-body">{member.bio}</p>
          )}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <ParentLayout>
        <div className="max-w-7xl mx-auto px-4 py-8">
          <div className="animate-pulse space-y-4">
            <div className="h-8 bg-gray-200 rounded w-48" />
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {[1,2,3,4,5,6].map(i => (
                <div key={i} className="h-48 bg-gray-200 rounded-xl" />
              ))}
            </div>
          </div>
        </div>
      </ParentLayout>
    );
  }

  return (
    <ParentLayout>
      <div className="max-w-7xl mx-auto px-4 py-6 sm:py-8">
        {/* Header - Athletic Style */}
        <div className="mb-6">
          <div className="flex items-center space-x-3 mb-2">
            <div className="w-12 h-12 bg-navy rounded-xl flex items-center justify-center shadow-md">
              <Users className="w-6 h-6 text-gold" />
            </div>
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-gold">Park City Chiefs</p>
              <h1 className="font-display text-2xl sm:text-3xl font-black text-navy uppercase tracking-tighter">Staff Directory</h1>
            </div>
          </div>
        </div>

        {/* Department Contacts Section - Athletic Card */}
        {departments.length > 0 && (
          <div className="mb-8 rounded-xl p-6 text-white relative overflow-hidden">
            {/* Field Background */}
            <div 
              className="absolute inset-0 bg-cover bg-center"
              style={{ backgroundImage: `url('/field-bg-2.jpeg')` }}
            />
            {/* Light Navy Gradient Overlay (right to left, 5% to 1%) */}
            <div className="absolute inset-0 bg-gradient-to-l from-navy/[0.01] via-navy/[0.03] to-navy/[0.05]" />
            {/* Base navy overlay for text readability */}
            <div className="absolute inset-0 bg-navy/70" />
            <div className="absolute top-0 right-0 w-32 h-32 bg-gold/10 rounded-full -translate-y-16 translate-x-16"></div>
            <h2 className="font-display text-lg font-black uppercase tracking-tight mb-4 flex items-center relative">
              <Mail className="w-5 h-5 mr-2 text-gold" />
              Department Contacts
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 relative">
              {departments
                .filter(dept => VISIBLE_DEPT_CONTACTS.includes(dept.name))
                .map(dept => (
                <a
                  key={dept.department_id}
                  href={`mailto:${dept.email}`}
                  className="bg-white/10 hover:bg-white/20 border border-white/10 rounded-lg p-4 transition-all duration-200 hover:-translate-y-0.5 hover:shadow-lg"
                  data-testid={`dept-contact-${dept.department_id}`}
                >
                  <h3 className="font-bold text-gold">{dept.name}</h3>
                  <p className="text-white/90 text-sm mt-1 font-body">{dept.email}</p>
                  {dept.description && (
                    <p className="text-white/60 text-xs mt-2 font-body">{dept.description}</p>
                  )}
                </a>
              ))}
            </div>
          </div>
        )}

        {/* Search & Filter - Athletic Style */}
        <div className="mb-6 space-y-3">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                type="text"
                placeholder="Search staff..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 border-gray-200 focus:border-navy"
                data-testid="staff-search"
              />
            </div>
            <Button
              variant="outline"
              onClick={() => setShowFilters(!showFilters)}
              className="sm:w-auto border-2 border-gray-200 hover:border-navy font-bold uppercase tracking-wider text-xs"
            >
              <Filter className="w-4 h-4 mr-2" />
              Filter
              <ChevronDown className={`w-4 h-4 ml-2 transition-transform ${showFilters ? 'rotate-180' : ''}`} />
            </Button>
          </div>

          {showFilters && (
            <div className="flex flex-wrap gap-2 p-4 bg-gray-50 rounded-xl border border-gray-200">
              <button
                onClick={() => setFilterDept('all')}
                className={`px-4 py-2 rounded-lg text-xs font-bold uppercase tracking-wider transition-all duration-200 ${
                  filterDept === 'all' ? 'bg-navy text-white' : 'bg-white border border-gray-200 text-gray-600 hover:border-navy'
                }`}
              >
                All
              </button>
              {Object.entries(DEPARTMENT_CONFIG).map(([key, config]) => (
                <button
                  key={key}
                  onClick={() => setFilterDept(key)}
                  className={`px-4 py-2 rounded-lg text-xs font-bold uppercase tracking-wider transition-all duration-200 ${
                    filterDept === key ? `${config.color} text-white` : 'bg-white border border-gray-200 text-gray-600 hover:border-navy'
                  }`}
                >
                  {config.label}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Staff Directory */}
        {Object.keys(filteredGrouped).length === 0 ? (
          <div className="text-center py-12 bg-white rounded-xl border border-gray-200">
            <Users className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <h3 className="font-display text-lg font-black text-gray-500 uppercase">No Staff Found</h3>
            <p className="text-gray-400 text-sm font-body">Try adjusting your search or filters</p>
          </div>
        ) : (
          <div className="space-y-8">
            {/* Board of Directors */}
            {filteredGrouped.board?.length > 0 && (
              <section>
                <div className="flex items-center space-x-2 mb-4">
                  <Shield className="w-5 h-5 text-navy" />
                  <h2 className="font-display text-lg font-black text-navy uppercase tracking-tight">Board of Directors</h2>
                  <span className="text-xs font-bold text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">{filteredGrouped.board.length}</span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {filteredGrouped.board.map(member => (
                    <StaffCard key={member.staff_id} member={member} />
                  ))}
                </div>
              </section>
            )}

            {/* Coaching Staff */}
            {filteredGrouped.coaching?.length > 0 && (
              <section>
                <div className="flex items-center space-x-2 mb-4">
                  <Award className="w-5 h-5 text-red" />
                  <h2 className="font-display text-lg font-black text-navy uppercase tracking-tight">Coaching Staff</h2>
                  <span className="text-xs font-bold text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">{filteredGrouped.coaching.length}</span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {filteredGrouped.coaching.map(member => (
                    <StaffCard key={member.staff_id} member={member} />
                  ))}
                </div>
              </section>
            )}

            {/* Operations */}
            {filteredGrouped.operations?.length > 0 && (
              <section>
                <div className="flex items-center space-x-2 mb-4">
                  <Briefcase className="w-5 h-5 text-light-blue" />
                  <h2 className="font-display text-lg font-black text-navy uppercase tracking-tight">Operations</h2>
                  <span className="text-xs font-bold text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">{filteredGrouped.operations.length}</span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {filteredGrouped.operations.map(member => (
                    <StaffCard key={member.staff_id} member={member} />
                  ))}
                </div>
              </section>
            )}

            {/* Volunteer Leadership */}
            {filteredGrouped.volunteer?.length > 0 && (
              <section>
                <div className="flex items-center space-x-2 mb-4">
                  <Heart className="w-5 h-5 text-gold" />
                  <h2 className="font-display text-lg font-black text-navy uppercase tracking-tight">Volunteer Leadership</h2>
                  <span className="text-xs font-bold text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">{filteredGrouped.volunteer.length}</span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {filteredGrouped.volunteer.map(member => (
                    <StaffCard key={member.staff_id} member={member} />
                  ))}
                </div>
              </section>
            )}
          </div>
        )}
      </div>
    </ParentLayout>
  );
};

export default StaffDirectoryPage;
